import { useCallback } from "react";
import {
  TE_STATE_CANCELLED,
  TE_STATE_FINALIZED,
  TE_STATE_PAUSED,
  TE_STATE_STARTED,
} from "../utils/coachExerciseNavigation";
import { buildAutoSequenceStartPayload } from "../utils/liveTrainingSettings";
import { noteExerciseTimerRevision } from "../utils/sessionSync";

/**
 * When the exercise is not running (no in-memory timer on the server), APIs send null/0.
 * Initialize display timers from planned sequence durations so the coach sees real values before Start.
 */
export function deriveCoachControlIdleTimers(ex) {
  const stateId = ex.tbl_state_te?.st_id || ex.te_state;
  const isRunning = stateId === TE_STATE_STARTED || stateId === "started";
  const isPaused = stateId === TE_STATE_PAUSED || stateId === "paused";
  const isFinalized = stateId === TE_STATE_FINALIZED || stateId === "finalized";
  const isCancelled = stateId === TE_STATE_CANCELLED || stateId === "cancelled";

  if (isFinalized || isCancelled) {
    return { timer: 0, sequenceTimer: 0 };
  }
  if (isRunning || isPaused || ex.synchronizedStartTime) {
    return null;
  }

  const sequences = ex.sequences || [];
  const totalSec =
    sequences.length > 0
      ? sequences.reduce(
          (s, sq) => s + (parseInt(sq.sequence_duration, 10) || 0) * 60,
          0
        )
      : (parseInt(ex.te_duration, 10) || 0) * 60;

  const idx =
    ex.currentSequence != null &&
    ex.currentSequence >= 0 &&
    ex.currentSequence < sequences.length
      ? ex.currentSequence
      : 0;
  const seqSec =
    sequences.length > 0
      ? (parseInt(sequences[idx]?.sequence_duration, 10) || 0) * 60
      : totalSec;

  return { timer: totalSec, sequenceTimer: seqSec };
}

/** Join ack is optional: the page already joined, and old/proxy paths may never ack. */
function joinAckBlocksAction(joinAck) {
  if (!joinAck || joinAck.ok !== false) return false;
  if (
    joinAck.code === "join_timeout" ||
    joinAck.code === "join_failed" ||
    joinAck.code === "session_not_found"
  ) {
    return false;
  }
  return true;
}

function isSocketErrorPayload(data) {
  if (!data) return false;
  if (data.error_code) return true;
  return typeof data.error === "string" && data.error.trim().length > 0;
}

function isStartSuccessPayload(data, targetTeId) {
  if (!data || String(data.te_id) !== String(targetTeId)) return false;
  if (isSocketErrorPayload(data)) return false;
  const state = data.te_state || data.tbl_state_te?.st_id;
  return state === TE_STATE_STARTED || state === "started";
}

function waitForSocketConnected(socket, timeoutMs = 5000) {
  return new Promise((resolve) => {
    if (!socket) {
      resolve(false);
      return;
    }
    if (socket.connected || socket.active) {
      resolve(true);
      return;
    }
    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      socket.off("connect", onConnect);
      resolve(ok);
    };
    const onConnect = () => finish(true);
    const timer = window.setTimeout(
      () => finish(!!(socket.connected || socket.active)),
      timeoutMs
    );
    socket.once("connect", onConnect);
    if (socket.connected || socket.active) finish(true);
  });
}

function applyAckToRefresh(te_id, ack, requestRefresh) {
  if (!requestRefresh || !te_id) return ack;
  if (ack?.exerciseStateUpdate) {
    noteExerciseTimerRevision(te_id, ack.exerciseStateUpdate.exercise_timer_revision);
    requestRefresh(te_id, ack.exerciseStateUpdate);
    return ack;
  }
  if (ack?.ok !== false) {
    requestRefresh(te_id);
  }
  return ack;
}

/** Server fans wave actions out per table (up to ~8s each), so allow a generous window. */
const COACH_ACTION_ACK_TIMEOUT_MS = 12000;

/**
 * Emit a coach action and settle on ack or timeout, so a lost ack can never leave
 * buttons / guards stuck. On timeout, ask for a fresh status to resync the UI.
 */
function emitCoachAction(socket, event, payload, { requestRefresh, teId } = {}) {
  return new Promise((resolve) => {
    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      requestRefresh?.(teId);
      resolve({ ok: false, code: "ack_timeout" });
    }, COACH_ACTION_ACK_TIMEOUT_MS);
    socket.emit(event, payload, (ack) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      const result = applyAckToRefresh(teId, ack, requestRefresh);
      resolve(result || ack || { ok: true });
    });
  });
}

function readUserId() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.user_id || null;
  } catch {
    return null;
  }
}

/** Re-assert coach role in the session room before coach-only socket actions. */
export function emitCoachJoinTrainingSession(socket, tsId, teId = null) {
  return new Promise((resolve) => {
    if (!socket || !tsId) {
      resolve({ ok: false, code: "missing_socket_or_te_id" });
      return;
    }
    const user_id = readUserId();
    if (!user_id) {
      resolve({ ok: false, code: "missing_user" });
      return;
    }
    const payload = {
      ts_id: tsId,
      user_id,
      role: "coach",
      active_te_id: teId || undefined,
    };
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve(result);
    };
    const timer = window.setTimeout(() => {
      finish({ ok: false, code: "join_timeout" });
    }, 1500);
    socket.emit("joinTrainingSession", payload, (ack) => {
      if (ack && ack.ok === false) {
        finish({ ok: false, code: ack.code || "join_failed", ...ack });
        return;
      }
      finish({ ok: true, ...(ack || {}) });
    });
  });
}

/** Coalesce double-taps: one in-flight emit per te_id, plus short post-ack cooldown. */
const COACH_ACTION_COOLDOWN_MS = 1500;
const finalizeGuards = new Map();
const startNextSequenceGuards = new Map();
const skipSequenceGuards = new Map();
const restartGuards = new Map();

function runTeIdGuardedAction(guardMap, teId, run) {
  const key = String(teId);
  const now = Date.now();
  const existing = guardMap.get(key);
  if (existing) {
    if (existing.pending && existing.promise) return existing.promise;
    if (now < (existing.cooldownUntil || 0)) {
      return Promise.resolve(
        existing.lastResult ?? { ok: false, code: "action_in_flight" }
      );
    }
  }

  let settle;
  const promise = new Promise((resolve) => {
    settle = resolve;
  });
  guardMap.set(key, { pending: true, promise, cooldownUntil: 0, lastResult: null });

  Promise.resolve()
    .then(() => run())
    .then((result) => {
      settle(result);
      if (result?.ok === false || result?.status === "error") {
        guardMap.delete(key);
        return;
      }
      guardMap.set(key, {
        pending: false,
        promise,
        cooldownUntil: Date.now() + COACH_ACTION_COOLDOWN_MS,
        lastResult: result,
      });
        setTimeout(() => {
          const cur = guardMap.get(key);
          if (cur && cur.promise === promise && !cur.pending) {
            guardMap.delete(key);
          }
        }, COACH_ACTION_COOLDOWN_MS);
    })
    .catch((err) => {
      const result = { ok: false, error: err?.message || String(err) };
      settle(result);
      guardMap.delete(key);
    });

  return promise;
}

/**
 * Shared socket emits for Coach Exercise Control and Training floating bar.
 * Coach actions use Socket.IO ack callbacks (full exerciseStateUpdate payload).
 */
export function useCoachExerciseLiveActions({
  socket,
  tsId,
  requestRefresh,
  coachRole = false,
  teId = null,
}) {
  const ensureCoachRole = useCallback(() => {
    if (!coachRole) return Promise.resolve({ ok: true, skipped: true });
    return emitCoachJoinTrainingSession(socket, tsId, teId);
  }, [coachRole, socket, tsId, teId]);

  const startExercise = useCallback(
    (targetTeId, onResult) => {
      if (!socket || !targetTeId) {
        onResult?.({ ok: false, code: "missing_socket_or_te_id" });
        return;
      }

      let settled = false;
      let fallbackTimer = null;
      const finish = (result) => {
        if (settled) return;
        settled = true;
        socket.off("exerciseStateUpdate", onStateUpdate);
        if (fallbackTimer) window.clearTimeout(fallbackTimer);
        onResult?.(result);
      };

      const onStateUpdate = (data) => {
        if (String(data?.te_id) !== String(targetTeId)) return;
        if (isSocketErrorPayload(data)) {
          finish({
            ok: false,
            error: data.error,
            error_code: data.error_code,
            code: data.error_code,
          });
          return;
        }
        if (isStartSuccessPayload(data, targetTeId)) {
          applyAckToRefresh(
            targetTeId,
            { ok: true, exerciseStateUpdate: data },
            requestRefresh
          );
          finish({ ok: true, exerciseStateUpdate: data });
        }
      };

      socket.on("exerciseStateUpdate", onStateUpdate);

      Promise.resolve()
        .then(() => waitForSocketConnected(socket))
        .then((connected) => {
          // Still emit while reconnecting — Socket.IO buffers packets.
          if (!connected && !socket) {
            finish({ ok: false, code: "socket_disconnected" });
            return null;
          }
          return ensureCoachRole();
        })
        .then((joinAck) => {
          if (settled || joinAck == null) return;
          if (joinAckBlocksAction(joinAck)) {
            finish(joinAck);
            return;
          }
          fallbackTimer = window.setTimeout(() => {
            finish({ ok: false, code: "start_timeout" });
          }, 12000);
          socket.emit(
            "startWave",
            {
              te_id: targetTeId,
              ...buildAutoSequenceStartPayload(tsId),
            },
            (ack) => {
              if (ack?.ok === false || isSocketErrorPayload(ack)) {
                finish({
                  ok: false,
                  code: ack?.code || ack?.error_code,
                  error: ack?.error,
                  error_code: ack?.error_code,
                  ...ack,
                });
                return;
              }
              const result = applyAckToRefresh(targetTeId, ack, requestRefresh);
              finish({ ok: true, ...(result || ack || {}) });
            }
          );
        })
        .catch((err) => {
          finish({
            ok: false,
            code: "start_failed",
            error: err?.message || String(err),
          });
        });
    },
    [socket, tsId, requestRefresh, ensureCoachRole]
  );

  const resumeExercise = useCallback(
    (targetTeId, remainingTime, onResult) => {
      if (!socket || !targetTeId) {
        onResult?.({ ok: false, code: "missing_socket_or_te_id" });
        return;
      }
      Promise.resolve(ensureCoachRole()).then(async (joinAck) => {
        if (joinAckBlocksAction(joinAck)) {
          onResult?.(joinAck);
          return;
        }
        const out = await emitCoachAction(
          socket,
          "resumeWave",
          {
            te_id: targetTeId,
            remainingTime,
            ...buildAutoSequenceStartPayload(tsId),
          },
          { requestRefresh, teId: targetTeId }
        );
        onResult?.(out);
      });
    },
    [socket, tsId, requestRefresh, ensureCoachRole]
  );

  const pauseExercise = useCallback(
    (targetTeId, onResult) => {
      if (!socket || !targetTeId) {
        onResult?.({ ok: false, code: "missing_socket_or_te_id" });
        return;
      }
      Promise.resolve(ensureCoachRole()).then(async (joinAck) => {
        if (joinAckBlocksAction(joinAck)) {
          onResult?.(joinAck);
          return;
        }
        const out = await emitCoachAction(
          socket,
          "pauseWave",
          { te_id: targetTeId },
          { requestRefresh, teId: targetTeId }
        );
        onResult?.(out);
      });
    },
    [socket, requestRefresh, ensureCoachRole]
  );

  const startNextSequence = useCallback(
    (targetTeId, onResult) => {
      if (!socket || !targetTeId) {
        onResult?.({ ok: false, code: "missing_socket_or_te_id" });
        return Promise.resolve({ ok: false, code: "missing_socket_or_te_id" });
      }
      // Coalesced taps share one promise; report its result to every caller.
      const promise = runTeIdGuardedAction(startNextSequenceGuards, targetTeId, async () => {
        const joinAck = await ensureCoachRole();
        if (joinAckBlocksAction(joinAck)) return joinAck;
        return emitCoachAction(
          socket,
          "startNextTrainingSequence",
          { te_id: targetTeId },
          { requestRefresh, teId: targetTeId }
        );
      });
      promise.then((out) => onResult?.(out));
      return promise;
    },
    [socket, requestRefresh, ensureCoachRole]
  );

  const skipSequence = useCallback(
    (targetTeId, onResult) => {
      if (!socket || !targetTeId) {
        onResult?.({ ok: false, code: "missing_socket_or_te_id" });
        return Promise.resolve({ ok: false, code: "missing_socket_or_te_id" });
      }
      // Coalesced taps share one promise; report its result to every caller.
      const promise = runTeIdGuardedAction(skipSequenceGuards, targetTeId, async () => {
        const joinAck = await ensureCoachRole();
        if (joinAckBlocksAction(joinAck)) return joinAck;
        return emitCoachAction(
          socket,
          "skipTrainingSequence",
          { te_id: targetTeId },
          { requestRefresh, teId: targetTeId }
        );
      });
      promise.then((out) => onResult?.(out));
      return promise;
    },
    [socket, requestRefresh, ensureCoachRole]
  );

  const finalizeExercise = useCallback(
    (targetTeId) => {
      if (!socket || !targetTeId) {
        return Promise.resolve({ ok: false, code: "missing_socket_or_te_id" });
      }
      return runTeIdGuardedAction(finalizeGuards, targetTeId, async () => {
        const joinAck = await ensureCoachRole();
        if (joinAckBlocksAction(joinAck)) return joinAck;
        return new Promise((resolve) => {
          socket.emit("finalizeExercise", { te_id: targetTeId }, (response) => {
            if (response?.exerciseStateUpdate) {
              noteExerciseTimerRevision(
                targetTeId,
                response.exerciseStateUpdate.exercise_timer_revision
              );
              requestRefresh?.(targetTeId, response.exerciseStateUpdate);
            } else if (response?.status === "success") {
              requestRefresh?.(targetTeId);
            }
            resolve(response);
          });
        });
      });
    },
    [socket, requestRefresh, ensureCoachRole]
  );

  const restartExercise = useCallback(
    (targetTeId) => {
      if (!socket || !targetTeId) {
        return Promise.resolve({ ok: false, code: "missing_socket_or_te_id" });
      }
      return runTeIdGuardedAction(restartGuards, targetTeId, async () => {
        const joinAck = await ensureCoachRole();
        if (joinAckBlocksAction(joinAck)) return joinAck;
        return new Promise((resolve) => {
          socket.emit("restartExercise", { te_id: targetTeId }, (response) => {
            if (response?.exerciseStateUpdate) {
              noteExerciseTimerRevision(
                targetTeId,
                response.exerciseStateUpdate.exercise_timer_revision
              );
              requestRefresh?.(targetTeId, response.exerciseStateUpdate);
            } else if (response?.status === "success" || response?.ok) {
              requestRefresh?.(targetTeId);
            }
            resolve(response);
          });
        });
      });
    },
    [socket, requestRefresh, ensureCoachRole]
  );

  return {
    startExercise,
    resumeExercise,
    pauseExercise,
    startNextSequence,
    skipSequence,
    finalizeExercise,
    restartExercise,
    ensureCoachRole,
  };
}
